import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Zap, ZapOff, SwitchCamera, Upload, Keyboard, RefreshCw } from "lucide-react";
import { createNativeDetector, createZxingReader, normalizeProductCode } from "../lib/barcode";

const SCAN_INTERVAL_MS = 180;
const HINT_AFTER_MS = 30000;

const ERRORS = {
  insecure: {
    title: "Camera needs a secure connection",
    text: "Live scanning only works over HTTPS. Open the site via https:// or use photo upload / manual entry.",
  },
  NotAllowedError: {
    title: "Camera access blocked",
    text: "Allow camera access for this site: tap the lock / “aA” icon in the address bar → Site settings → Camera → Allow, then retry.",
  },
  NotFoundError: {
    title: "No camera found",
    text: "This device doesn't expose a camera to the browser. Upload a photo of the barcode or type the number instead.",
  },
  NotReadableError: {
    title: "Camera is busy",
    text: "Another app or tab is using the camera. Close it and retry.",
  },
  OverconstrainedError: {
    title: "Camera settings unsupported",
    text: "We couldn't open the camera with the requested settings. Retry to use relaxed settings.",
  },
  default: {
    title: "Couldn't start the camera",
    text: "Something went wrong while opening the camera. Retry, upload a photo, or type the barcode.",
  },
};

function mapError(e) {
  const name = e?.name || "default";
  return { name, ...(ERRORS[name] || ERRORS.default) };
}

export const BarcodeScanner = ({ open, onClose, onDetected, onUpload, onManual }) => {
  const [phase, setPhase] = useState("starting");
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [torch, setTorch] = useState({ supported: false, on: false });
  const [zoom, setZoom] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [decoder, setDecoder] = useState("");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const runIdRef = useRef(0);
  const loopRef = useRef(null);
  const hintRef = useRef(null);
  const candidateRef = useRef(null);
  const currentDeviceRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;

  const stopAll = useCallback(() => {
    runIdRef.current += 1;
    clearTimeout(loopRef.current);
    clearTimeout(hintRef.current);
    candidateRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const accept = useCallback(
    (code) => {
      console.log("[scanner] accepted code", code);
      stopAll();
      try {
        navigator.vibrate?.(80);
      } catch {}
      onDetectedRef.current(code);
    },
    [stopAll]
  );

  const startLoop = useCallback(
    (runId, detector, reader) => {
      let busy = false;
      const canvas = canvasRef.current || (canvasRef.current = document.createElement("canvas"));
      const tick = async () => {
        if (runId !== runIdRef.current) return;
        const video = videoRef.current;
        if (video && video.readyState >= 2 && video.videoWidth > 0 && !busy) {
          busy = true;
          try {
            let raw = null;
            let fmt = null;
            if (detector) {
              const codes = await detector.detect(video);
              if (codes.length) {
                raw = codes[0].rawValue;
                fmt = codes[0].format;
              }
            } else {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              canvas.getContext("2d", { willReadFrequently: true }).drawImage(video, 0, 0);
              const result = reader.decodeFromCanvas(canvas);
              raw = result.getText();
              fmt = result.getBarcodeFormat();
            }
            if (raw != null) {
              const code = normalizeProductCode(raw, fmt);
              if (code) {
                if (candidateRef.current === code) {
                  accept(code);
                  return;
                }
                candidateRef.current = code;
              } else {
                candidateRef.current = null;
              }
            }
          } catch {
            // NotFoundException on frames without a barcode — expected
          }
          busy = false;
        }
        if (runId === runIdRef.current) loopRef.current = setTimeout(tick, SCAN_INTERVAL_MS);
      };
      loopRef.current = setTimeout(tick, SCAN_INTERVAL_MS);
    },
    [accept]
  );

  const start = useCallback(
    async (deviceId = null, relaxed = false) => {
      stopAll();
      const runId = runIdRef.current;
      setError(null);
      setShowHint(false);
      setPhase("starting");

      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        setError({ name: "insecure", ...ERRORS.insecure });
        setPhase("error");
        return;
      }

      const constraints = deviceId
        ? { video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false }
        : relaxed
        ? { video: true, audio: false }
        : {
            video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false,
          };

      console.log("[scanner] start", { deviceId, relaxed });
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        console.error("[scanner] getUserMedia failed:", e?.name, e?.message);
        if (runId !== runIdRef.current) return;
        if ((e?.name === "OverconstrainedError" || e?.name === "ConstraintNotSatisfiedError") && !relaxed) {
          return start(null, true);
        }
        setError(mapError(e));
        setPhase("error");
        return;
      }
      if (runId !== runIdRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;

      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings?.() || {};
      currentDeviceRef.current = settings.deviceId || deviceId;
      console.log("[scanner] camera:", track.label, settings.facingMode, `${settings.width}x${settings.height}`);

      let videoInputs = [];
      try {
        videoInputs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
      } catch {}
      if (runId !== runIdRef.current) return;
      setDevices(videoInputs);

      if (!deviceId && !relaxed && videoInputs.length > 1 && settings.facingMode && settings.facingMode !== "environment") {
        const back = videoInputs.find((d) => /back|rear|environment/i.test(d.label));
        if (back && back.deviceId !== settings.deviceId) {
          console.log("[scanner] facingMode ignored, switching to", back.label);
          return start(back.deviceId);
        }
      }

      try {
        await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
      } catch {}
      const caps = track.getCapabilities?.() || {};
      setTorch({ supported: !!caps.torch, on: false });
      if (caps.zoom && caps.zoom.max > caps.zoom.min) {
        setZoom({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1, value: settings.zoom || caps.zoom.min });
      } else {
        setZoom(null);
      }

      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      try {
        await video.play();
      } catch (e) {
        console.warn("[scanner] video.play() rejected:", e?.message);
      }
      if (video.readyState < 2) {
        await new Promise((res) => {
          const done = () => {
            video.removeEventListener("loadedmetadata", done);
            video.removeEventListener("playing", done);
            res();
          };
          video.addEventListener("loadedmetadata", done);
          video.addEventListener("playing", done);
          setTimeout(done, 3000);
        });
      }
      if (runId !== runIdRef.current) return;

      const detector = await createNativeDetector();
      const reader = detector ? null : createZxingReader();
      const decoderName = detector ? "BarcodeDetector" : "zxing";
      console.log("[scanner] decoder:", decoderName);
      setDecoder(decoderName);
      setPhase("scanning");
      startLoop(runId, detector, reader);
      hintRef.current = setTimeout(() => setShowHint(true), HINT_AFTER_MS);
    },
    [stopAll, startLoop]
  );

  useEffect(() => {
    if (!open) return undefined;
    start();
    const onVis = () => {
      if (document.hidden) {
        console.log("[scanner] tab hidden, pausing");
        stopAll();
        setPhase("paused");
      } else {
        start(currentDeviceRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      document.body.style.overflow = prevOverflow;
      stopAll();
    };
  }, [open, start, stopAll]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch.on }] });
      setTorch((t) => ({ ...t, on: !t.on }));
    } catch (e) {
      console.warn("[scanner] torch failed:", e?.message);
    }
  };

  const changeZoom = async (value) => {
    const track = streamRef.current?.getVideoTracks()[0];
    setZoom((z) => ({ ...z, value }));
    try {
      await track?.applyConstraints({ advanced: [{ zoom: value }] });
    } catch {}
  };

  const switchCamera = () => {
    if (devices.length < 2) return;
    const idx = devices.findIndex((d) => d.deviceId === currentDeviceRef.current);
    const next = devices[(idx + 1) % devices.length];
    start(next.deviceId);
  };

  const close = () => {
    stopAll();
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      data-testid="scanner-modal"
      className="fixed inset-0 z-[1000] bg-black text-white flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <video
        ref={videoRef}
        data-testid="scan-video"
        className="absolute inset-0 w-full h-full object-cover"
        playsInline
        muted
        autoPlay
      />

      {phase === "scanning" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            data-testid="scan-frame"
            className="relative w-[86vw] max-w-[440px] aspect-[2.2/1] rounded-lg"
            style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.62)" }}
          >
            {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2", "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"].map((c) => (
              <span key={c} className={`absolute w-7 h-7 border-[color:var(--lime)] ${c}`} />
            ))}
            <motion.div
              className="absolute left-2 right-2 h-[2px] rounded-full"
              style={{ background: "#A8FF3E", boxShadow: "0 0 12px 2px rgba(168,255,62,0.7)" }}
              animate={{ top: ["6%", "92%", "6%"] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>
      )}

      <div className="relative flex items-center justify-between px-3 pt-3">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/70 pl-1" data-testid="scanner-decoder">
          {phase === "scanning" ? `Live scan · ${decoder}` : "Barcode scanner"}
        </div>
        <button
          onClick={close}
          aria-label="Close scanner"
          data-testid="scanner-close"
          className="w-11 h-11 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex-1 flex flex-col justify-end items-center px-4 pb-4 gap-3">
        {phase === "starting" && (
          <div data-testid="scanner-starting" className="mb-auto mt-[40vh] text-sm text-white/80 flex items-center gap-2">
            <RefreshCw className="h-4 w-4 animate-spin text-[color:var(--lime)]" /> Starting camera…
          </div>
        )}
        {phase === "paused" && (
          <div className="mb-auto mt-[40vh] text-sm text-white/80">Paused — return to this tab to resume.</div>
        )}

        {phase === "error" && error && (
          <div data-testid="scanner-error" className="mb-auto mt-[22vh] max-w-sm w-full surface rounded-xl p-5 text-[color:var(--text)]">
            <div className="font-display font-semibold text-lg mb-1">{error.title}</div>
            <p className="text-sm text-[color:var(--text-2)] mb-4">{error.text}</p>
            <div className="grid grid-cols-1 gap-2">
              {error.name !== "NotFoundError" && error.name !== "insecure" && (
                <button data-testid="scanner-retry" onClick={() => start(null, error.name === "OverconstrainedError")} className="lime-btn rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2">
                  <RefreshCw className="h-4 w-4" /> Retry
                </button>
              )}
              <button data-testid="scanner-error-upload" onClick={() => { close(); onUpload(); }} className="ghost-btn rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2">
                <Upload className="h-4 w-4" /> Upload a photo
              </button>
              <button data-testid="scanner-error-manual" onClick={() => { close(); onManual(); }} className="ghost-btn rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2">
                <Keyboard className="h-4 w-4" /> Enter barcode manually
              </button>
            </div>
          </div>
        )}

        {phase === "scanning" && (
          <>
            <p data-testid="scanner-helper" className="text-center text-sm text-white/85 max-w-xs">
              Point the camera at the barcode. Hold steady, 10–20 cm away, good light.
            </p>
            {showHint && (
              <p data-testid="scanner-hint" className="text-center text-xs text-[color:var(--lime)] max-w-xs">
                Having trouble? Try the torch, move closer, or upload a photo.
              </p>
            )}
            {zoom && (
              <div className="w-full max-w-xs flex items-center gap-3 text-xs text-white/70">
                <span>1×</span>
                <input
                  data-testid="scanner-zoom"
                  type="range"
                  min={zoom.min}
                  max={zoom.max}
                  step={zoom.step}
                  value={zoom.value}
                  onChange={(e) => changeZoom(parseFloat(e.target.value))}
                  className="flex-1 accent-[#A8FF3E]"
                />
                <span>{Math.round(zoom.max)}×</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {torch.supported && (
                <button data-testid="scanner-torch" onClick={toggleTorch} aria-label="Toggle torch" className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur transition-colors ${torch.on ? "bg-[color:var(--lime)] text-black" : "bg-white/10 hover:bg-white/20"}`}>
                  {torch.on ? <Zap className="h-5 w-5" /> : <ZapOff className="h-5 w-5" />}
                </button>
              )}
              {devices.length > 1 && (
                <button data-testid="scanner-switch" onClick={switchCamera} aria-label="Switch camera" className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center transition-colors">
                  <SwitchCamera className="h-5 w-5" />
                </button>
              )}
              <button data-testid="scanner-upload" onClick={() => { close(); onUpload(); }} className="h-12 px-4 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-sm inline-flex items-center gap-2 transition-colors">
                <Upload className="h-4 w-4" /> Photo
              </button>
              <button data-testid="scanner-manual" onClick={() => { close(); onManual(); }} className="h-12 px-4 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-sm inline-flex items-center gap-2 transition-colors">
                <Keyboard className="h-4 w-4" /> Type it
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default BarcodeScanner;
